import React, { useEffect, useMemo, useState } from 'react';
import { Close, Search } from '@mui/icons-material';
import {
  Box,
  Chip,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from '@mui/material';

const formatPrice = (value) => {
  if (value === undefined || value === null) return 'Q -';
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (isNaN(num)) return 'Q -';
  const formatted = new Intl.NumberFormat('es-GT', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
  return `Q ${formatted}`;
};

const SummaryRow = ({ label, value, strong = false, color }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      columnGap: 2,
      alignItems: 'baseline',
      py: 0.35,
    }}
  >
    <Typography
      variant="subtitle2"
      sx={{
        fontSize: '0.9rem',
        textAlign: 'right',
        fontWeight: strong ? 700 : 500,
        whiteSpace: 'normal',
        maxWidth: 360,
        lineHeight: 1.2,
      }}
    >
      {label}
    </Typography>
    <Typography
      variant="body2"
      sx={{
        fontSize: '0.9rem',
        textAlign: 'right',
        fontWeight: strong ? 700 : 400,
        color: color || 'text.primary',
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {value}
    </Typography>
  </Box>
);

const normalizeText = (value) =>
  String(value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();

const extractValueText = (value) => {
  if (value == null) return '';
  if (Array.isArray(value)) {
    return value.map(extractValueText).filter(Boolean).join(' ');
  }
  if (typeof value === 'object') {
    const candidate =
      value.description ??
      value.name ??
      value.code ??
      value.id ??
      value.value ??
      value.label ??
      value.text;
    if (candidate == null) return '';
    if (Array.isArray(candidate)) {
      return candidate.map(extractValueText).filter(Boolean).join(' ');
    }
    if (typeof candidate === 'object') return '';
    return String(candidate);
  }
  return String(value);
};

function CollapsedProductsTable({
  data = [],
  columns = [],
  renderExtras,
  pagination = false,
  rowsPerPage: initialRowsPerPage = 10,
  summary,
  enableSearch = false,
  searchPlaceholder = 'Buscar...',
  searchKeys,
  searchDebounceMs = 200
}) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(initialRowsPerPage);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const handleChangePage = (_, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (!enableSearch) return;
    if (!searchTerm) {
      setDebouncedSearch('');
      return;
    }
    const handle = setTimeout(() => setDebouncedSearch(searchTerm), searchDebounceMs);
    return () => clearTimeout(handle);
  }, [searchTerm, searchDebounceMs, enableSearch]);

  useEffect(() => {
    if (!pagination || !enableSearch) return;
    setPage(0);
  }, [debouncedSearch, enableSearch, pagination]);

  const effectiveSearchKeys = useMemo(() => {
    if (Array.isArray(searchKeys) && searchKeys.length > 0) return searchKeys;
    const keys = (columns || []).map((col) => col.key).filter(Boolean);
    return keys;
  }, [searchKeys, columns]);

  const searchTokens = useMemo(() => {
    if (!enableSearch) return [];
    const text = normalizeText(debouncedSearch);
    if (!text) return [];
    return text.split(/\s+/).filter(Boolean);
  }, [debouncedSearch, enableSearch]);

  const indexedData = useMemo(() => {
    if (!enableSearch) return [];
    const keys = effectiveSearchKeys;
    return (data || []).map((row) => ({
      row,
      searchText: normalizeText(
        keys.map((key) => extractValueText(row?.[key])).filter(Boolean).join(' ')
      ),
    }));
  }, [data, enableSearch, effectiveSearchKeys]);

  const filteredData = useMemo(() => {
    if (!enableSearch) return data;
    if (searchTokens.length === 0) return data;
    return indexedData
      .filter(({ searchText }) => searchTokens.every((token) => searchText.includes(token)))
      .map(({ row }) => row);
  }, [data, enableSearch, indexedData, searchTokens]);

  const paginatedData = pagination
    ? filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
    : filteredData;

  const showAbono = summary && summary.abono !== undefined && summary.abono !== null;
  const showSaldoReal = summary && summary.saldoReal !== undefined && summary.saldoReal !== null;
  const saldoRealNote = summary?.saldoRealNote || '';

  return (
    <Paper elevation={1} sx={{ borderRadius: 2, overflow: 'hidden', border: '1px solid', borderColor: '#e6eaf2' }}>
      {enableSearch && (
        <Box sx={{ px: 2, pt: 2, pb: 1 }}>
          <TextField
            size="small"
            variant="outlined"
            fullWidth
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => setSearchTerm('')}
                    aria-label="Limpiar búsqueda"
                  >
                    <Close fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        </Box>
      )}
      <TableContainer>
        <Table size="small" sx={{
          '& thead th': { borderBottom: '1px solid #0e1440', background: '#10173c' },
          '& tbody tr:nth-of-type(odd)': { backgroundColor: 'rgba(16, 23, 60, 0.03)' },
          '& tbody tr:hover': { backgroundColor: 'rgba(16, 23, 60, 0.08)' },
          '& td': { borderBottom: '1px solid #eef2f7' }
        }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: 'white' }}>#</TableCell>
              {columns.map((col, idx) => (
                <TableCell
                  key={idx}
                  align={col.align || 'left'}
                  sx={{ color: 'white' }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => {
              const claimStatus = row.claim_status ?? row.promo_claim_status ?? row.claim?.status ?? null;
              const deliveredLaterInvoiceNumber =
                row.delivered_later_invoice_number ??
                row.deliveredLaterInvoiceNumber ??
                row.claim?.delivered_later_invoice_number ??
                '';
              const rawClaimNote =
                row.claim_note ??
                row.promo_claim_note ??
                row.claim?.note ??
                '';
              const claimNote =
                (claimStatus === 'DELIVERED_LATER' || claimStatus === 'RECEIVED_LATER') && deliveredLaterInvoiceNumber
                  ? `Enviado en factura ${deliveredLaterInvoiceNumber}`
                  : rawClaimNote;
              const isClaimedRow = claimStatus === 'CLAIMED';
              const rowSx = isClaimedRow
                ? { '& td': { textDecoration: 'line-through', color: 'text.disabled', opacity: 0.7 } }
                : undefined;

              return (
              <React.Fragment key={`row-${index}`}>
                <TableRow sx={rowSx}>
                  <TableCell>
                    <Box
                      sx={{
                        display: 'inline-block',
                        px: 1,
                        py: '2px',
                        backgroundColor: '#EEF1F6',
                        color: '#2B3E4D',
                        borderRadius: '8px',
                        fontWeight: 600,
                        fontSize: '0.8rem',
                        textAlign: 'center',
                      }}
                    >
                      {pagination ? page * rowsPerPage + index + 1 : index + 1}
                    </Box>
                  </TableCell>
                  {columns.map((col, colIdx) => (
                    <TableCell key={colIdx} align={col.align || 'left'}>
                      {col.render
                        ? col.render(row, pagination ? page * rowsPerPage + index : index)
                        : (() => {
                            const value = row[col.key];

                            // NUNCA renderizar objetos, siempre convertir a string
                            if (value == null) return '-';

                            let renderedValue = value;
                            if (typeof value === 'object') {
                              // Extraer propiedad seg??n tipo de campo
                              if (col.key === 'description') {
                                renderedValue = value.description || value.name || '';
                              } else if (col.key === 'code') {
                                renderedValue = value.code || '';
                              } else if (col.key === 'prformat') {
                                renderedValue = value.name || value.prformat || '';
                              } else if (col.key === 'price' || col.key === 'finalPrice' || col.key === 'subtotal') {
                                // Usar formatPrice que ya existe
                                const num = typeof value === 'object' ? value.price || value.value || 0 : value;
                                renderedValue = formatPrice(num);
                              } else {
                                // Para cualquier otro objeto, extraer string segura
                                renderedValue = String(value.id || value.value || '');
                              }
                            } else if (Array.isArray(value)) {
                              renderedValue = value.map(item => String(item)).join(', ');
                            } else {
                              renderedValue = String(value);
                            }

                            const validStatus =
                              claimStatus === 'CLAIMED' ||
                              claimStatus === 'RECEIVED_LATER' ||
                              claimStatus === 'DELIVERED_LATER' ||
                              claimStatus === 'DELIVERED_HERE' ||
                              claimStatus === 'PENDING';
                            if (col.key === 'description' && validStatus) {
                              const isDeliveredLater = claimStatus === 'DELIVERED_LATER' || claimStatus === 'RECEIVED_LATER';
                              const statusLabel = claimStatus === 'CLAIMED'
                                ? 'Reclamado'
                                : claimStatus === 'DELIVERED_HERE'
                                  ? 'Completa'
                                  : isDeliveredLater
                                    ? 'Enviado'
                                    : 'A reclamar ahora';
                              const statusColor = claimStatus === 'CLAIMED'
                                ? 'success'
                                : claimStatus === 'DELIVERED_HERE'
                                  ? 'success'
                                  : isDeliveredLater
                                    ? 'info'
                                    : 'warning';
                              return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.35 }}>
                                  <Typography variant="body2" sx={{ fontSize: '0.85rem' }}>
                                    {renderedValue}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
                                    <Chip
                                      size="small"
                                      color={statusColor}
                                      label={statusLabel}
                                      sx={{ height: 20, fontSize: '0.65rem', textDecoration: 'none' }}
                                    />
                                    {claimNote && (
                                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                        {claimNote}
                                      </Typography>
                                    )}
                                  </Box>
                                </Box>
                              );
                            }

                            return renderedValue;
                          })()}
                    </TableCell>
                  ))}
                </TableRow>

                {renderExtras &&
                  renderExtras(row, pagination ? page * rowsPerPage + index : index)}
              </React.Fragment>
            )})}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          component="div"
          count={filteredData.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Filas por pagina:"
          rowsPerPageOptions={[5, 10, 20, 50]}
        />
      )}

      {summary && (
        <Paper sx={{ mt: 1, p: 2, background: 'rgba(16, 23, 60, 0.04)', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ width: 'min(520px, 100%)' }}>
              <SummaryRow label="Subtotal:" value={formatPrice(summary.subTotal)} />
              <SummaryRow label="Producto no recibido:" value={formatPrice(summary.descuentoProductoNoEntregado)} />
              {showAbono && (
                <SummaryRow label="Abono:" value={formatPrice(summary.abono)} />
              )}
              {showSaldoReal && (
                <SummaryRow
                  label={`Saldo real${saldoRealNote ? ` (${saldoRealNote})` : ''}:`}
                  value={formatPrice(summary.saldoReal)}
                />
              )}
              <SummaryRow
                label={summary.labelTotal || 'Total a pagar'}
                value={formatPrice(summary.total)}
                strong
                color="#0d47a1"
              />
            </Box>
          </Box>
        </Paper>
      )}


    </Paper>
  );
}

export default React.memo(CollapsedProductsTable, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.columns === nextProps.columns &&
    prevProps.renderExtras === nextProps.renderExtras &&
    prevProps.pagination === nextProps.pagination &&
    prevProps.rowsPerPage === nextProps.rowsPerPage &&
    prevProps.summary === nextProps.summary &&
    prevProps.enableSearch === nextProps.enableSearch &&
    prevProps.searchPlaceholder === nextProps.searchPlaceholder &&
    prevProps.searchKeys === nextProps.searchKeys &&
    prevProps.searchDebounceMs === nextProps.searchDebounceMs
  );
});
